import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Divider,
    IconButton,
    Tooltip,
    Avatar,
    Collapse,
    Stack,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SaveIcon from "@mui/icons-material/Save";
import PsychologyIcon from "@mui/icons-material/Psychology";
import EmailIcon from "@mui/icons-material/Email";
import ChatIcon from "@mui/icons-material/Chat";
import CallIcon from "@mui/icons-material/Call";
import LocationOnIcon from "@mui/icons-material/LocationOn";

/** Utility */
const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/** Generate multi-month, multi-venue timeline */
const generateAgentTimeline = (agent) => {
    const base = new Date("2025-08-15T09:00:00");
    const venues = [
        { name: "Roosevelt Park", city: "Los Angeles" },
        { name: "Pasadena YMCA", city: "Pasadena" },
        { name: "Brooklyn Community Gym", city: "New York" },
        { name: "Detroit Rec Center", city: "Detroit" },
    ];
    const randomVenue = () => venues[Math.floor(Math.random() * venues.length)];
    const addDays = (days, hours) => {
        const d = new Date(base);
        d.setDate(d.getDate() + days);
        d.setHours(hours);
        return d;
    };

    return [
        {
            id: 1,
            type: "email",
            venue: venues[0],
            timestamp: addDays(0, 9),
            icon: <EmailIcon fontSize="small" />,
            title: "Initial Inquiry Email",
            sender: `${agent}@united.ai`,
            content: `Hi Rosa,\n\nWe’re looking to rent your gym at Roosevelt Park next weekend for a youth basketball session. Are there openings between 3–7 PM?\n\nThanks,\n${agent}`,
        },
        {
            id: 2,
            type: "system",
            venue: venues[0],
            timestamp: addDays(0, 10),
            icon: <PsychologyIcon fontSize="small" />,
            title: "System Context Update",
            content: `Agent saved contact record for Roosevelt Park. Linked prior successful sessions from June 2025.`,
        },
        {
            id: 3,
            type: "sms",
            venue: venues[1],
            timestamp: addDays(4, 13),
            icon: <ChatIcon fontSize="small" />,
            title: "Pasadena YMCA Text Outreach",
            content: `Hi team, this is ${agent} from United. Any gym availability on Sept 3rd, 5–8 PM?`,
        },
        {
            id: 4,
            type: "sms",
            venue: venues[1],
            timestamp: addDays(4, 13.4),
            icon: <ChatIcon fontSize="small" />,
            title: "Venue Response (Pasadena YMCA)",
            content: `Hi ${agent}, we have that time open! Rate is $125/hr. Would you like us to reserve it?`,
        },
        {
            id: 5,
            type: "email",
            venue: venues[1],
            timestamp: addDays(5, 11),
            icon: <EmailIcon fontSize="small" />,
            title: "Booking Confirmation Email",
            content: `Yes, please reserve that slot — insurance and payment will be processed tomorrow.\n\n– ${agent}`,
        },
        {
            id: 6,
            type: "call",
            venue: venues[2],
            timestamp: addDays(10, 15),
            icon: <CallIcon fontSize="small" />,
            title: "Phone Call with Brooklyn Community Gym",
            audioUrl: "/sample-call-1.mp3",
            transcript: [
                { speaker: "Agent", text: "Hi, just confirming you received our updated insurance form." },
                { speaker: "Manager", text: "Yes, I’ve got it here. Everything looks good for next Tuesday." },
                { speaker: "Agent", text: "Perfect, thank you! We’ll send setup details this afternoon." },
            ],
        },
        {
            id: 7,
            type: "system",
            venue: venues[2],
            timestamp: addDays(11, 9),
            icon: <PsychologyIcon fontSize="small" />,
            title: "Memory Recall Event",
            content: `Agent referenced August correspondence with Brooklyn Gym. Adjusted negotiation tone per prior feedback on brevity.`,
        },
        {
            id: 8,
            type: "call",
            venue: venues[3],
            timestamp: addDays(30, 14),
            icon: <CallIcon fontSize="small" />,
            title: "Detroit Rec Center Coordination Call",
            audioUrl: "/sample-call-2.mp3",
            transcript: [
                { speaker: "Agent", text: "Hi, checking availability for late October youth training sessions." },
                { speaker: "Coordinator", text: "We can accommodate that, just send over your insurance and rosters." },
                { speaker: "Agent", text: "Will do, thanks for confirming!" },
            ],
        },
        {
            id: 9,
            type: "system",
            venue: venues[3],
            timestamp: addDays(32, 10),
            icon: <PsychologyIcon fontSize="small" />,
            title: "Training Adjustment",
            content: `Agent reduced response latency by 63% compared to August communications. System confidence increased to 94%.`,
        },
    ];
};

const AgentCommunicationPanel = ({ open, onClose, agent, viewMode = "timeline" }) => {
    const [timeline, setTimeline] = useState([]);
    const [expandedFeedback, setExpandedFeedback] = useState({});
    const [feedbackText, setFeedbackText] = useState({});
    const [isPlaying, setIsPlaying] = useState(null);
    const audioRefs = useRef({});

    useEffect(() => {
        if (open && agent) setTimeline(generateAgentTimeline(agent));
    }, [open, agent]);

    const toggleFeedback = (id) =>
        setExpandedFeedback((prev) => ({ ...prev, [id]: !prev[id] }));

    const handleFeedbackSubmit = (id) => {
        if (!feedbackText[id]?.trim()) return;
        alert(`✅ Feedback added for item ${id}:\n"${feedbackText[id]}"`);
        setFeedbackText((prev) => ({ ...prev, [id]: "" }));
        setExpandedFeedback((prev) => ({ ...prev, [id]: false }));
    };

    const handlePlayToggle = (id) => {
        if (isPlaying === id) {
            audioRefs.current[id]?.pause();
            setIsPlaying(null);
        } else {
            if (isPlaying && audioRefs.current[isPlaying])
                audioRefs.current[isPlaying].pause();
            audioRefs.current[id]?.play();
            setIsPlaying(id);
        }
    };

    const visibleTimeline =
        viewMode === "callOnly"
            ? timeline.filter((t) => t.type === "call")
            : viewMode === "emailOnly"
                ? timeline.filter((t) => t.type === "email")
                : timeline;

    if (!open) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <Paper
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90vw",
                    maxWidth: 900,
                    maxHeight: "90vh",
                    overflowY: "auto",
                    bgcolor: "#111",
                    color: "#fff",
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid #333",
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" sx={{ color: "#12c099", fontWeight: 600 }}>
                        {agent} — {viewMode === "timeline" ? "Activity Timeline" : "Channel View"}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {visibleTimeline.map((item, index) => {
                    const prevDate =
                        index > 0
                            ? formatDate(visibleTimeline[index - 1].timestamp)
                            : null;
                    const currentDate = formatDate(item.timestamp);
                    const showDateHeader = currentDate !== prevDate;

                    return (
                        <React.Fragment key={item.id}>
                            {showDateHeader && (
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: "#12c099",
                                        mt: index > 0 ? 2 : 0,
                                        mb: 1,
                                        fontWeight: 600,
                                        borderBottom: "1px solid #333",
                                    }}
                                >
                                    📅 {currentDate}
                                </Typography>
                            )}

                            <Paper
                                sx={{
                                    bgcolor: "#181818",
                                    border: "1px solid #222",
                                    borderRadius: 2,
                                    p: 2,
                                    mb: 2,
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                    <Avatar
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            bgcolor:
                                                item.type === "email"
                                                    ? "#1976d2"
                                                    : item.type === "sms"
                                                        ? "#388e3c"
                                                        : item.type === "call"
                                                            ? "#ffb300"
                                                            : "#9c27b0",
                                        }}
                                    >
                                        {item.icon}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: "#fff" }}>
                                            <b>{item.title}</b>
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#999" }}>
                                            {formatTime(item.timestamp)}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {item.venue && (
                                    <Chip
                                        icon={<LocationOnIcon sx={{ color: "#fff !important" }} />}
                                        label={`${item.venue.name} (${item.venue.city})`}
                                        size="small"
                                        sx={{
                                            bgcolor: "#222",
                                            color: "#12c099",
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                    />
                                )}

                                {/* CONTENT */}
                                {item.content && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#ccc",
                                            mt: 1,
                                            whiteSpace: "pre-wrap",
                                            fontFamily: "monospace",
                                        }}
                                    >
                                        {item.content}
                                    </Typography>
                                )}

                                {/* CALL HANDLING */}
                                {item.type === "call" && (
                                    <Box sx={{ mt: 2 }}>
                                        <audio
                                            ref={(el) => (audioRefs.current[item.id] = el)}
                                            src={item.audioUrl}
                                            onEnded={() => setIsPlaying(null)}
                                            controls
                                            style={{ width: "100%", borderRadius: 4 }}
                                        />
                                        <Box sx={{ mt: 1 }}>
                                            {item.transcript.map((line, i) => (
                                                <Typography
                                                    key={i}
                                                    variant="body2"
                                                    sx={{ color: "#ccc", fontFamily: "monospace", mb: 0.5 }}
                                                >
                                                    <b>{line.speaker}:</b> {line.text}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* FEEDBACK */}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 1.5, color: "#12c099", borderColor: "#12c099" }}
                                    onClick={() =>
                                        toggleFeedback(item.id)
                                    }
                                >
                                    {expandedFeedback[item.id] ? "Hide Feedback" : "Add Feedback"}
                                </Button>

                                <Collapse in={expandedFeedback[item.id]} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            placeholder={`Add feedback for ${item.title}...`}
                                            value={feedbackText[item.id] || ""}
                                            onChange={(e) =>
                                                setFeedbackText((prev) => ({
                                                    ...prev,
                                                    [item.id]: e.target.value,
                                                }))
                                            }
                                            sx={{
                                                bgcolor: "#121212",
                                                borderRadius: 1,
                                                mb: 1,
                                                "& .MuiOutlinedInput-root fieldset": { borderColor: "#333" },
                                                "& .MuiInputBase-input": { color: "#fff" },
                                            }}
                                        />
                                        <Button
                                            startIcon={<SaveIcon />}
                                            variant="contained"
                                            sx={{ bgcolor: "#12c099", color: "#000", fontWeight: 600 }}
                                            onClick={() => handleFeedbackSubmit(item.id)}
                                        >
                                            Submit Feedback
                                        </Button>
                                    </Box>
                                </Collapse>
                            </Paper>
                        </React.Fragment>
                    );
                })}

                <Divider sx={{ borderColor: "#333", my: 2 }} />
                <Typography variant="caption" sx={{ color: "#888" }}>
                    All communications logged over multiple months, across regions and venues, with agent memory synchronized for retraining.
                </Typography>
            </Paper>
        </Modal>
    );
};

export default AgentCommunicationPanel;
